import { message, Modal } from "antd";
import { Alert } from "components/elements";
import useOptions from "features/masterData/hooks/useOptions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { generateRandomString } from "utils/helper";
import * as services from "../services/employeeApi";
import { setCurrentContactInfo, setCurrentContactInfoOnEdit, setIsLoading } from "../slices/employeeSlice";
const toInitialValues = (initialValues = {}) => initialValues;

const toParams = (values = {}) => ({ ...values });

function useEmployeeContactInfo() {
  const dispatch = useDispatch();
  const params = useParams();
  const employeeId = params.id;

  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.employee.isLoading);
  const currentContactInfo = useSelector((state) => state.employee.currentContactInfo);
  const currentContactInfoOnEdit = useSelector((state) => state.employee.currentContactInfoOnEdit);

  const {
    relationshipOptions,
    getRelationshipOptions,
    sourceProvinceOptions = [],
    sourceDistrictOptions = [],
    sourceSubDistrictOptions = [],
    getProvinceOptions,
    getDistrictOptions,
    getSubDistrictOptions,
    clearDistrictOptions,
    clearSubDistrictOptions,
    registered_sourceProvinceOptions = [],
    registered_sourceDistrictOptions = [],
    registered_sourceSubDistrictOptions = [],
    getRegisteredProvinceOptions,
    getRegisteredDistrictOptions,
    getRegisteredSubDistrictOptions,
    clearRegisteredDistrictOptions,
    clearRegisteredSubDistrictOptions,
  } = useOptions();

  const [isEditing, setIsEditing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [contactInfo, setContactInfo] = useState({});

  const initialValues = useMemo(() => {
    if (isEditing) {
      setContactInfo(currentContactInfoOnEdit)
      return toInitialValues(currentContactInfoOnEdit)
    }
    if (isVisible) {
      setContactInfo(currentContactInfoOnEdit)
      return toInitialValues(currentContactInfoOnEdit)
    } else {
      setContactInfo(currentContactInfo)
      return toInitialValues(currentContactInfo)
    }
  }, [currentContactInfo, currentContactInfoOnEdit, isVisible, isEditing]);

  const mapOptions = useCallback((sourceOptions, labelKey, valueKey) => {
    return sourceOptions.map((opt) => ({
      ...opt,
      label: opt[labelKey],
      value: opt[valueKey],
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const provinceOptionsTh = useMemo(() => mapOptions(sourceProvinceOptions, 'provinceName', 'provinceId'), [sourceProvinceOptions, mapOptions]);
  const provinceOptionsEn = useMemo(() => mapOptions(sourceProvinceOptions, 'provinceNameEn', 'provinceId'), [sourceProvinceOptions, mapOptions]);
  const districtOptionsTh = useMemo(() => mapOptions(sourceDistrictOptions, 'districtName', 'districtId'), [sourceDistrictOptions, mapOptions]);
  const districtOptionsEn = useMemo(() => mapOptions(sourceDistrictOptions, 'districtNameEn', 'districtId'), [sourceDistrictOptions, mapOptions]);
  const subDistrictOptionsTh = useMemo(() => mapOptions(sourceSubDistrictOptions, 'subDistrictName', 'subDistrictId'), [sourceSubDistrictOptions, mapOptions]);
  const subDistrictOptionsEn = useMemo(() => mapOptions(sourceSubDistrictOptions, 'subDistrictNameEn', 'subDistrictId'), [sourceSubDistrictOptions, mapOptions]);
  const registeredProvinceOptionsTh = useMemo(() => mapOptions(registered_sourceProvinceOptions, 'provinceName', 'provinceId'), [registered_sourceProvinceOptions, mapOptions]);
  const registeredProvinceOptionsEn = useMemo(() => mapOptions(registered_sourceProvinceOptions, 'provinceNameEn', 'provinceId'), [registered_sourceProvinceOptions, mapOptions]);
  const registeredDistrictOptionsTh = useMemo(() => mapOptions(registered_sourceDistrictOptions, 'districtName', 'districtId'), [registered_sourceDistrictOptions, mapOptions]);
  const registeredDistrictOptionsEn = useMemo(() => mapOptions(registered_sourceDistrictOptions, 'districtNameEn', 'districtId'), [registered_sourceDistrictOptions, mapOptions]);
  const registeredSubDistrictOptionsTh = useMemo(() => mapOptions(registered_sourceSubDistrictOptions, 'subDistrictName', 'subDistrictId'), [registered_sourceSubDistrictOptions, mapOptions]);
  const registeredSubDistrictOptionsEn = useMemo(() => mapOptions(registered_sourceSubDistrictOptions, 'subDistrictNameEn', 'subDistrictId'), [registered_sourceSubDistrictOptions, mapOptions]);

  const onEdit = useCallback(() => {
    setIsEditing(true)
    getContactInformationOnEdit(params.id, true, true)
  }, []);
  const { t } = useTranslation();

  const onCancel = useCallback(() => {
    getContactInformation(params.id, false, false)
    handleVisableAndEdit(false)
  }, []);

  const handleToggleVisibility = () => {
    setIsVisible((prev) => {
      const update = !prev
      getContactInformation(params.id, update, false)
      return update;
    });
  };

  const handleVisableAndEdit = (flag) => {
    setIsEditing(flag)
    setIsVisible(flag)
  }


  const fetchContactInformation = async (employeeId, visible = false, edit = false) => {
    try {
      dispatch(setIsLoading(true));
      let response = await services.getContactInformation({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId,
        visible: visible,
        isEdit: edit
      });

      const data = response.data;
      const modifiedValues = {
        ...data,
        sameRegisterAddress: data.registerAddressPermit,
        relationshipId: data.relationship?.id,
        registerProvinceId: data.registerProvince?.id,
        registerDistrictId: data.registerDistrict?.id,
        registerSubDistrictId: data.registerSubDistrict?.id,
        provinceId: data.province?.id,
        districtId: data.district?.id,
        subDistrictId: data.subDistrict?.id,
        fullRegisterAddress: `${data.registerAddress} ${data.registerSubDistrict?.name} ${data.registerDistrict?.name} ${data.registerProvince?.name} ${data.registerZipCode}`,
        fullRegisterAddressEn: `${data.registerAddressEn} ${data.registerSubDistrict?.nameEn} ${data.registerDistrict?.nameEn} ${data.registerProvince?.nameEn} ${data.registerZipCode}`,
        fullAddress: `${data.address} ${data.subDistrict?.name} ${data.district?.name} ${data.province?.name} ${data.zipCode}`,
        fullAddressEn: `${data.addressEn} ${data.subDistrict?.nameEn} ${data.district?.nameEn} ${data.province?.nameEn} ${data.zipCode}`
      };
      getDistrictOptions({ provinceId: data.province?.code });
      getSubDistrictOptions({ districtId: data.district?.code });
      getRegisteredProvinceOptions();
      getRegisteredDistrictOptions({ provinceId: data.registerProvince?.code });
      getRegisteredSubDistrictOptions({ districtId: data.registerDistrict?.code });

      if (visible) {
        dispatch(setCurrentContactInfoOnEdit(modifiedValues || {}));
      } else {
        dispatch(setCurrentContactInfo(modifiedValues || {}));
      }
    } catch (error) {
      Alert({ type: "error", resultObject: error });
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const getContactInformation = useCallback((employeeId, visible, edit) => fetchContactInformation(employeeId, visible, edit), []);

  const getContactInformationOnEdit = useCallback((employeeId, visible, edit) => fetchContactInformation(employeeId, visible, edit), []);


  const updateContactInformation = useCallback(async (values) => {
    try {
      dispatch(setIsLoading(true));
      const requestBody = toParams({
        ...values,
        registerAddressPermit: values?.sameRegisterAddress,
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: params.id,
        address: values?.sameRegisterAddress === "Y" ? values?.registerAddress : values?.address,
        addressEn: values?.sameRegisterAddress === "Y" ? values?.registerAddressEn : values?.addressEn,
        provinceId: values?.sameRegisterAddress === "Y" ? values?.registerProvinceId : values?.provinceId,
        districtId: values?.sameRegisterAddress === "Y" ? values?.registerDistrictId : values?.districtId,
        subDistrictId: values?.sameRegisterAddress === "Y" ? values?.registerSubDistrictId : values?.subDistrictId,
        zipCode: values?.sameRegisterAddress === "Y" ? values?.registerZipCode : values?.zipCode,
        relationshipId: values?.relationshipId ? values?.relationshipId : null,
        emergencyMobileCode: values?.emergencyMobileCode ? values?.emergencyMobileCode : null
      });
      await services.updateContactInformation(requestBody);
      message.success(t("common.success"))
      getContactInformation(params.id);
      handleVisableAndEdit(false);
    } catch (error) {
      function contentDetail(params) {
        return error?.data?.status?.details?.map?.(o => {
          return <div>{o?.key} {o?.value}</div>
        })
      }

      Modal.error({ content: (contentDetail()), okText: t("confirm") })
      // Alert({ type: "error", resultObject: error });
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch, getContactInformation, params.id]);

  const onSubmit = useCallback((values) => {
    Modal.confirm({
      title: t('generalInfo.confirmation'),
      content: (
        <>
          <p>{t('generaInfo.are-you-sure-confirm-to-edit')}</p>
        </>
      ),
      onOk() {
        // console.log("🚀 ~ onOk ~ values:", values)

        const modifiedValues = {
          ...values,
          employeeId: employeeId,
          companyId: currentCompany
        }
        updateContactInformation(modifiedValues);
      },
      okText: t('confirm'),
      cancelText: t('generalInfo.cancel'),
    });
  }, [currentCompany, employeeId, updateContactInformation]);

  const updateContactInfoProfile = useCallback(async (values) => {
    try {
      dispatch(setIsLoading(true));
      const requestBody = toParams({
        ...values,
        registerAddressPermit: values?.sameRegisterAddress,
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: params.id,
        address: values?.sameRegisterAddress === "Y" ? values?.registerAddress : values?.address,
        addressEn: values?.sameRegisterAddress === "Y" ? values?.registerAddressEn : values?.addressEn,
        provinceId: values?.sameRegisterAddress === "Y" ? values?.registerProvinceId : values?.provinceId,
        districtId: values?.sameRegisterAddress === "Y" ? values?.registerDistrictId : values?.districtId,
        subDistrictId: values?.sameRegisterAddress === "Y" ? values?.registerSubDistrictId : values?.subDistrictId,
        zipCode: values?.sameRegisterAddress === "Y" ? values?.registerZipCode : values?.zipCode,
      });
      await services.updateContactInfoProfile(requestBody);
      message.success(t("common.success"))
      getContactInformation(params.id);
      handleVisableAndEdit(false);
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch, getContactInformation, params.id]);

  const onSubmitEditProfile = useCallback((values) => {
    Modal.confirm({
      title: t('generalInfo.confirmation'),
      content: (
        <>
          <p>{t('generaInfo.are-you-sure-confirm-to-edit')}</p>
        </>
      ),
      onOk() {
        const modifiedValues = {
          ...values,
          employeeId: employeeId,
          companyId: currentCompany
        }
        updateContactInfoProfile(modifiedValues);
      },
      okText: t('confirm'),
      cancelText: t('generalInfo.cancel'),
    });
  }, [currentCompany, employeeId, updateContactInfoProfile]);

  useEffect(() => {
    if (params.id && currentCompany) getContactInformation(params.id);
  }, [currentCompany, params.id, getContactInformation]);

  useEffect(() => {
    getRelationshipOptions();
  }, [getRelationshipOptions]);

  const handleProviceChange = useCallback((val) => {
    clearDistrictOptions();
    clearSubDistrictOptions();
    if (val) {
      getDistrictOptions({ provinceId: val });
    }
  }, [clearDistrictOptions, clearSubDistrictOptions, getDistrictOptions]);

  const handleRegisteredProviceChange = useCallback((val) => {
    clearRegisteredDistrictOptions();
    clearRegisteredSubDistrictOptions();
    if (val) {
      getRegisteredDistrictOptions({ provinceId: val });
    }
  }, [clearRegisteredDistrictOptions, clearRegisteredSubDistrictOptions, getRegisteredDistrictOptions]);

  const handleDistrictChange = useCallback((val) => {
    clearSubDistrictOptions();
    if (val) {
      getSubDistrictOptions({ districtId: val });
    }
  }, [clearSubDistrictOptions, getSubDistrictOptions]);

  const handleRegisteredDistrictChange = useCallback((val) => {
    clearRegisteredSubDistrictOptions();
    if (val) {
      getRegisteredSubDistrictOptions({ districtId: val });
    }
  }, [clearRegisteredSubDistrictOptions, getRegisteredSubDistrictOptions]);

  useEffect(() => {
    getProvinceOptions();
  }, [getProvinceOptions]);

  // useEffect(() => {
  //   getRegisteredProvinceOptions();
  // }, [getRegisteredProvinceOptions]);

  return {
    isLoading,
    isEditing,
    initialValues,
    relationshipOptions,
    provinceOptionsTh,
    provinceOptionsEn,
    districtOptionsTh,
    districtOptionsEn,
    subDistrictOptionsTh,
    subDistrictOptionsEn,
    handleProviceChange,
    handleDistrictChange,
    contactInfo: contactInfo,
    onEdit,
    onSubmit,
    onCancel,
    onSubmitEditProfile,
    registeredProvinceOptionsTh,
    registeredProvinceOptionsEn,
    registeredDistrictOptionsTh,
    registeredDistrictOptionsEn,
    registeredSubDistrictOptionsTh,
    registeredSubDistrictOptionsEn,
    handleRegisteredProviceChange,
    handleRegisteredDistrictChange,
    setIsVisible,
    isVisible,
    handleToggleVisibility
  };
}

export default useEmployeeContactInfo;
